import type { Brand, Locale } from "@borealis/layout-express";
import type { RecommendationsVariant } from "@finn-no/mobility-dealer-types";
import type { Breadcrumb } from "@m10s/mobility-mapping-utils";
import type { Consent, EidResponse } from "@mobility-item-page/types";
import type { Logger } from "pino";
import React from "react";
import type { ProGatewayFeatures } from "src/server/services/MobilityProGateway.ts";
import type { PersonalFinanceFeatures } from "src/types/personalFinance.ts";
import type { ContactTopic } from "../client/components/ev/contactTopics.ts";
import type { AmplitudeExperiments } from "../server/services/AmplitudeExperimentsService.ts";
import type { FeatureToggles } from "../types/unleash.ts";

export type GoogleMapsEmbed = {
  apiUrl: string;
  apiKey: string;
};

export type ClientAppConfig = {
  logger: Logger<never, boolean>;
  isMobile: boolean;
  locale: Locale;
  brand: Brand;
  mountOrigin: string;
  originalUrl: string;
  initialImage: number;
  isPreview: boolean;
  pfAdLookupLinks: PersonalFinanceFeatures | null;
  brandInfo: string;
  featureToggles: FeatureToggles;
  proGatewayFeatures: ProGatewayFeatures;
  recommendationsVariant?: RecommendationsVariant;
  breadcrumbs: Breadcrumb[];
  googleMapsEmbed: GoogleMapsEmbed | null;
  enableFeedback: boolean;
  openContactForm: boolean;
  /** Topic from `?contact=<topic>`, whose question the auto-opened contact form is prefilled with */
  contactTopic?: ContactTopic;

  // User
  logInId?: number;
  isLoggedIn: boolean;
  hideDetailedAddress: boolean;
  env: string;
  eidAuth: EidResponse;
  consent: Consent;

  // Experiments
  amplitudeExperiments: AmplitudeExperiments | undefined;
  helpCenter: string;
};

const AppConfigContext = React.createContext<ClientAppConfig | undefined>(undefined);

type AppConfigProviderProps = {
  children: React.ReactNode;
  appConfig: ClientAppConfig;
};

export function AppConfigProvider(props: AppConfigProviderProps) {
  const { children, appConfig } = props;

  return (
    <AppConfigContext value={{ ...appConfig, isLoggedIn: Boolean(appConfig.logInId) }}>{children}</AppConfigContext>
  );
}

export function useAppConfig() {
  const context = React.useContext(AppConfigContext);
  if (typeof context === "undefined") {
    throw new Error("useAppConfig must be used within an AppConfigProvider");
  }
  const isServer = typeof window === "undefined";
  return { ...context, logger: isServer && context.logger ? context.logger : console };
}
